import { useMemo, useState } from 'react'
import { Card, Failed, Loading } from '@/components/bits'
import { orgName, useOrg } from '@/org/OrgProvider'
import { useAsync } from '@/data/useAsync'
import { myRecentLogs, reportSickness, searchPeople } from '@/data/sickness'
import type { FrontDeskPerson, ShiftWriteStatus, SicknessLogged } from '@/data/sickness'
import { shortDate } from '@/lib/format'

/**
 * Logging a sickness call, as reception experiences it.
 *
 * The constraints on this screen are unusual, and every one of them is
 * deliberate.
 *
 * IT SHOWS A NAME AND A DEPARTMENT AND NOTHING ELSE. Not because the layout
 * is spare, but because front_desk holds no read access to any of the rest.
 * front_desk_people() returns four columns and there is no query on this screen
 * that could return a fifth.
 *
 * IT READS THE NAME BACK BEFORE WRITING. The Planday shift change is
 * immediate, and a shift type carries a salary code, so a wrong name at six in
 * the morning is somebody's pay. The confirm step exists so the person on the
 * phone hears the name repeated, which is what actually catches the error.
 *
 * IT SAYS WHICH DAYS ARE A GUESS. An expected return date creates a row for
 * every day up to it, and the days after today are a prediction. Reporting "5
 * days recorded" without saying that 4 of them have not happened would make a
 * guess look like a fact, which is the mistake this project keeps having to
 * design against.
 */

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

const WRITE_WORDS: Record<ShiftWriteStatus, string> = {
  not_applicable: 'No shift change queued for Planday.',
  pending: 'Queued to mark the shift SSP in Planday.',
  written: 'Shift marked SSP in Planday.',
  failed: 'Planday could not be updated. HR has been left a record.',
  reverted: 'A previous shift change was put back.',
}

function Recent() {
  const logs = useAsync((_signal) => myRecentLogs(), [])

  if (logs.state === 'loading') return <Loading what="what you logged today" />
  if (logs.state === 'error') return <Failed message={logs.message} onRetry={logs.reload} />
  if (logs.data.length === 0) {
    return (
      <p className="note">
        You have not logged anything in the last 24 hours. This list shows only what{' '}
        <strong>you</strong> logged, from this account, so a colleague's calls will not appear here.
      </p>
    )
  }

  return (
    <table className="grid">
      <thead>
        <tr>
          <th>Team member</th>
          <th>Department</th>
          <th>Day</th>
          <th>Logged</th>
        </tr>
      </thead>
      <tbody>
        {logs.data.map((l) => (
          <tr key={l.dayId}>
            <td>{l.fullName}</td>
            <td>{l.department ?? '—'}</td>
            <td>{shortDate(l.absenceDate)}</td>
            <td className="mono">{new Date(l.loggedAt).toLocaleTimeString('en-GB')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function FrontDesk() {
  const { current } = useOrg()
  const [term, setTerm] = useState('')
  const [chosen, setChosen] = useState<FrontDeskPerson | null>(null)
  const [firstDay, setFirstDay] = useState(today())
  const [expectedReturn, setExpectedReturn] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<SicknessLogged | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  const query = term.trim()
  const results = useAsync(
    (_signal) => (query.length < 2 ? Promise.resolve([]) : searchPeople(query)),
    [query],
  )

  /**
   * The days this will actually record, worked out as you type.
   *
   * "Expected date of return" means the day they come BACK, so entering
   * tomorrow records only today. That caught out the person who designed this
   * screen, on his own data, having written a sentence on the form explaining
   * it. A sentence does not fix an ambiguous field. Showing the consequence
   * does, so the screen now lists the dates it is about to record and counts
   * them.
   */
  const days = useMemo(() => {
    const out: string[] = []
    if (firstDay === '') return out
    const from = new Date(`${firstDay}T00:00:00Z`)
    if (expectedReturn === '' || expectedReturn <= firstDay) return [firstDay]
    const back = new Date(`${expectedReturn}T00:00:00Z`)
    for (let d = new Date(from); d < back; d.setUTCDate(d.getUTCDate() + 1)) {
      out.push(d.toISOString().slice(0, 10))
    }
    return out
  }, [firstDay, expectedReturn])

  const projected = useMemo(
    () => days.filter((d) => d > today()).length,
    [days],
  )

  function reset() {
    setChosen(null)
    setTerm('')
    setFirstDay(today())
    setExpectedReturn('')
    setConfirming(false)
    setDone(null)
    setError(null)
    setNonce((n) => n + 1)
  }

  async function save() {
    if (chosen === null) return
    setSaving(true)
    setError(null)
    try {
      const result = await reportSickness(
        chosen.personId,
        firstDay,
        expectedReturn === '' ? null : expectedReturn,
      )
      setDone(result)
      setConfirming(false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
      setConfirming(false)
    } finally {
      setSaving(false)
    }
  }

  /* ── done ─────────────────────────────────────────────── */

  if (done !== null) {
    return (
      <section className="screen">
        {/* An idempotent no-op used to render in the same panel as a fresh
            save, reading "nothing was changed" in small type under a heading
            saying "Sickness logged". That is how somebody concludes the button
            does nothing: they were told, quietly, in the middle of a success
            message. */}
        <h1>{done.alreadyLogged ? 'Already recorded' : 'Sickness logged'}</h1>
        <Card>
          <p className="lede">
            <strong>{done.fullName}</strong>
            {done.department !== null && <> · {done.department}</>}
          </p>
          {done.alreadyLogged ? (
            <p className="callout">
              <strong>{shortDate(done.absenceDate)} was already recorded for this person</strong>,
              so nothing has changed and nothing is duplicated. If they are off for longer, log the
              next day instead. If this looks wrong, tell HR rather than logging it again.
            </p>
          ) : (
            <p>
              Recorded from {shortDate(done.absenceDate)}
              {done.lastAbsentDate !== null && <> to {shortDate(done.lastAbsentDate)}</>}.
            </p>
          )}
          {done.daysSkipped > 0 && (
            <p className="callout">
              {done.daysSkipped} day{done.daysSkipped === 1 ? '' : 's'} in that range{' '}
              {done.daysSkipped === 1 ? 'was' : 'were'} already recorded on another absence, so{' '}
              {done.daysSkipped === 1 ? 'it was' : 'they were'} left alone. Nothing is duplicated.
              If that looks wrong, HR can see both on the sickness board.
            </p>
          )}
          {done.daysProjected > 0 && (
            <p className="note">
              {done.daysCreated} day{done.daysCreated === 1 ? '' : 's'} recorded, of which{' '}
              <strong>{done.daysProjected} have not happened yet</strong> and are based on the
              expected return date. If they come back earlier, the manager confirms the real date
              on the return to work form and those days are put back.
            </p>
          )}
          <p className="note">{WRITE_WORDS[done.shiftWriteStatus]}</p>
          {/* The wording here has been wrong in both directions. First it
              claimed the head of department had been notified when no email
              existed at all. Then it told reception to ring them, which became
              untrue the moment Resend was verified and the first email
              delivered.
              
              What it says now is what actually happens, without overclaiming:
              the email is QUEUED here and sent by the worker within a few
              minutes, so "has been told" would still be a shade ahead of the
              facts at the moment reception reads it. */}
          <p className="note">
            The head of department and HR are emailed automatically, usually within a few minutes.
            If somebody needs to arrange cover for a shift starting shortly, ring them as well
            rather than waiting for it.
          </p>
          <div className="actions">
            <button type="button" className="btn primary" onClick={reset}>
              Log another
            </button>
          </div>
        </Card>
      </section>
    )
  }

  /* ── confirm ──────────────────────────────────────────── */

  if (confirming && chosen !== null) {
    return (
      <section className="screen">
        <h1>Check this before it is saved</h1>
        <Card>
          <p className="note">
            Read the name back to the person on the phone. This marks the shift as SSP in Planday
            straight away, and a shift type decides what somebody is paid.
          </p>
          <dl className="readback">
            <dt>Team member</dt>
            <dd>
              <strong>{chosen.fullName}</strong>
            </dd>
            <dt>Department</dt>
            <dd>{chosen.department ?? 'Not recorded'}</dd>
            <dt>First day of sickness</dt>
            <dd>{shortDate(firstDay)}</dd>
            <dt>Days recorded as off</dt>
            <dd>
              <strong>
                {days.length} day{days.length === 1 ? '' : 's'}
              </strong>
              <span className="note">
                {' '}
                {days.length <= 4
                  ? days.map((d) => shortDate(d)).join(', ')
                  : `${shortDate(days[0]!)} to ${shortDate(days[days.length - 1]!)}`}
              </span>
            </dd>
            <dt>Back at work on</dt>
            <dd>
              {expectedReturn === '' ? (
                'They did not say'
              ) : (
                <>
                  {shortDate(expectedReturn)}
                  <span className="note"> · not recorded as an absence</span>
                </>
              )}
            </dd>
          </dl>
          <div className="actions">
            <button type="button" className="btn primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Yes, log this sickness'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={saving}
              onClick={() => setConfirming(false)}
            >
              Go back and change it
            </button>
          </div>
        </Card>
      </section>
    )
  }

  /* ── the form ─────────────────────────────────────────── */

  return (
    <section className="screen">
      <h1>Log a sickness call</h1>
      {/* Which hotel, in words. Somebody testing across two organisations, or
          covering reception at the other property, otherwise has no way to
          know which one they are writing to. */}
      <p className="eyebrow">{orgName(current)}</p>
      <p className="lede">
        For when a team member rings to say they are not fit for work. Record who and which days.
        Do not record why: reasons and medical details are for HR and the head of department.
      </p>

      {error !== null && <Failed message={error} />}

      <Card title="Who called in sick">
        {chosen === null ? (
          <>
            <label className="stackfield">
              <span>Search by name</span>
              <input
                type="search"
                autoFocus
                value={term}
                placeholder="Start typing a name"
                onChange={(e) => setTerm(e.target.value)}
              />
            </label>

            {query.length < 2 && <p className="note">Type at least two letters.</p>}
            {query.length >= 2 && results.state === 'loading' && <Loading what="matching people" />}
            {results.state === 'error' && (
              <Failed message={results.message} onRetry={results.reload} />
            )}
            {results.state === 'ready' && query.length >= 2 && (
              <>
                {results.data.length === 0 ? (
                  <p className="note">
                    Nobody matches that. Check the spelling, and if they are new they may not be on
                    the system yet, in which case tell HR rather than guessing at a similar name.
                  </p>
                ) : (
                  <ul className="picklist" key={nonce}>
                    {results.data.map((p) => (
                      <li key={p.personId}>
                        <button type="button" onClick={() => setChosen(p)}>
                          <span>{p.fullName}</span>
                          <span className="note">
                            {p.department ?? 'No department'} · {p.organisationCode}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <p className="lede">
              <strong>{chosen.fullName}</strong>
              {chosen.department !== null && <> · {chosen.department}</>}
            </p>
            <button type="button" className="btn" onClick={() => setChosen(null)}>
              Not them, search again
            </button>
          </>
        )}
      </Card>

      {chosen !== null && (
        <Card title="Which days">
          <label className="stackfield">
            <span>First day of sickness</span>
            <input
              type="date"
              value={firstDay}
              max={today()}
              onChange={(e) => setFirstDay(e.target.value)}
            />
          </label>

          <label className="stackfield">
            <span>Expected date of return, if they said</span>
            <input
              type="date"
              value={expectedReturn}
              min={firstDay}
              onChange={(e) => setExpectedReturn(e.target.value)}
            />
          </label>

          <p className="note">
            The expected return date is the day they come <strong>back</strong>, not their last day
            off. Leave it blank if they did not say: recording only today is better than guessing at
            a week.
          </p>

          <p className="callout">
            <strong>
              This records {days.length} day{days.length === 1 ? '' : 's'} off:
            </strong>{' '}
            {days.length <= 4
              ? days.map((d) => shortDate(d)).join(', ')
              : `${shortDate(days[0]!)} to ${shortDate(days[days.length - 1]!)}`}
            {expectedReturn !== '' && (
              <>
                . Back at work on {shortDate(expectedReturn)}, which is not recorded as an absence
              </>
            )}
            .
            {projected > 0 && (
              <>
                {' '}
                {projected} of {days.length === projected ? 'those' : 'them'} have not happened yet.
              </>
            )}
          </p>

          <div className="actions">
            <button
              type="button"
              className="btn primary"
              disabled={expectedReturn !== '' && expectedReturn <= firstDay}
              onClick={() => setConfirming(true)}
            >
              Continue
            </button>
          </div>
        </Card>
      )}

      <Card title="What you logged in the last 24 hours">
        <Recent />
      </Card>
    </section>
  )
}
